// ============================================================
// shared/effets.js — les règles CHIFFRÉES, appliquées par le code.
// ------------------------------------------------------------
// Décision de Thibaud (14 octobre 2026, docs/OUTIL-ASSISTANT.md §6) : une
// règle chiffrée — « sur l'inox, +20 % de cycle » — s'applique par le
// CALCUL, pas par le modèle. Elle ne passe pas dans un prompt : elle
// modifie, de façon déterministe et tracée, la gamme que l'agent a
// proposée, avant que le deviseur la voie.
//
// La grammaire est FERMÉE :
//   { cible:     temps_cycle | temps_reglage | mise_en_route | programmation
//                | controle | operation:<verbe du catalogue>,
//     operation: fixer_min | majorer_pct | ajouter_min | minimum_min,
//     valeur:    un nombre }
//
// L'ORDRE, validé par Thibaud avant d'écrire une ligne :
//   1. l'agent propose sa gamme (habitudes, analogie, marges : AVANT lui) ;
//   2. une ligne corrigée à la main garde son temps — aucun effet ne la
//      touche, elle n'entre même pas dans les totaux d'une cible ;
//   3. les effets s'appliquent aux autres lignes, par opération dans
//      l'ordre fixer → majorer → ajouter → minimum, et à opération égale
//      dans l'ordre de validation des règles. Le minimum passe en dernier :
//      un plancher n'est jamais redescendu par un effet suivant ;
//   4. chaque effet appliqué est TRACÉ, sur la ligne et en liste.
// ============================================================

import { z } from 'zod';

export const CIBLES_EFFET = ['temps_cycle', 'temps_reglage', 'mise_en_route', 'programmation', 'controle'];
export const OPERATIONS_EFFET = ['fixer_min', 'majorer_pct', 'ajouter_min', 'minimum_min'];

export const LIBELLES_CIBLE = {
  temps_cycle: 'cycle (production, par pièce)',
  temps_reglage: 'montage (dans la mise en route)',
  mise_en_route: 'mise en route',
  programmation: 'programmation (dans la mise en route)',
  controle: 'contrôle',
};

export const effetSchema = z.object({
  cible: z.string().trim().refine(
    (c) => CIBLES_EFFET.includes(c) || /^operation:\S.{0,60}$/.test(c),
    'cible inconnue : temps_cycle, temps_reglage, mise_en_route, programmation, controle ou operation:<verbe>',
  ),
  operation: z.enum(OPERATIONS_EFFET),
  // Une majoration peut être une MINORATION (−90 % au plus) ; un temps
  // fixé, ajouté ou plancher est positif — et borné, une faute de frappe
  // de trois zéros ne doit pas devenir une règle.
  valeur: z.number().finite(),
}).strict().superRefine((e, ctx) => {
  const v = e.valeur;
  const ok = e.operation === 'majorer_pct' ? v >= -90 && v <= 500 : v >= 0 && v <= 6000;
  if (!ok) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['valeur'],
      message: e.operation === 'majorer_pct' ? 'pourcentage entre −90 et 500' : 'minutes entre 0 et 6000',
    });
  }
});

const norm = (v) => String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
const r1 = (v) => Math.round(v * 10) / 10;
const fige = (l) => l?.source === 'corrige' || l?.source === 'consigne';

/** Une phrase pour l'écran : « cycle +20 % », « mise en route : 45 min au moins ». */
export function decrireEffet(e) {
  const cible = e.cible.startsWith('operation:') ? `opération « ${e.cible.slice(10)} »` : (LIBELLES_CIBLE[e.cible] ?? e.cible);
  if (e.operation === 'majorer_pct') return `${cible} ${e.valeur >= 0 ? '+' : '−'}${Math.abs(e.valeur)} %`;
  if (e.operation === 'ajouter_min') return `${cible} +${e.valeur} min`;
  if (e.operation === 'minimum_min') return `${cible} : ${e.valeur} min au moins`;
  return `${cible} fixé à ${e.valeur} min`;
}

/**
 * Les PARTS d'une gamme sur lesquelles une cible agit : l'index de la
 * ligne, et comment lire puis écrire la part concernée. La programmation
 * et le montage sont des PARTS d'une ligne de mise en route (§5) ; tout le
 * reste est le temps entier de la ligne.
 */
function partsDeLaCible(gamme, cible) {
  const entier = (i) => ({ i, lire: (l) => Number(l.temps_min) || 0, ecrire: (l, v) => ({ ...l, temps_min: v }) });
  const prog = (l) => Math.min(Number(l.programmation_incluse_min) || 0, Number(l.temps_min) || 0);
  const parts = [];
  gamme.forEach((l, i) => {
    if (fige(l)) return;
    const c = l.categorie;
    if (cible === 'temps_cycle' && (c === 'production' || c === 'cycle')) parts.push(entier(i));
    else if (cible === 'controle' && c === 'controle') parts.push(entier(i));
    else if (cible === 'mise_en_route' && ['prototype', 'montage', 'programmation'].includes(c)) parts.push(entier(i));
    else if (cible === 'programmation') {
      if (c === 'programmation') parts.push(entier(i));
      else if (c === 'prototype') {
        parts.push({
          i,
          lire: prog,
          // La part change, la ligne suit du même écart.
          ecrire: (ll, v) => ({
            ...ll, temps_min: (Number(ll.temps_min) || 0) - prog(ll) + v, programmation_incluse_min: v,
          }),
        });
      }
    } else if (cible === 'temps_reglage') {
      if (c === 'montage') parts.push(entier(i));
      else if (c === 'prototype') {
        parts.push({
          i,
          lire: (ll) => (Number(ll.temps_min) || 0) - prog(ll),
          ecrire: (ll, v) => ({ ...ll, temps_min: v + prog(ll) }),
        });
      }
    } else if (cible.startsWith('operation:')) {
      const verbe = norm(cible.slice(10));
      if (norm(l.operation) === verbe || norm(l.operation_label) === verbe || norm(l.libelle).startsWith(verbe)) {
        parts.push(entier(i));
      }
    }
  });
  return parts;
}

/** Les nouvelles valeurs des parts, pour une opération sur leur TOTAL. */
function nouvellesValeurs(valeurs, { operation, valeur }) {
  const total = valeurs.reduce((s, v) => s + v, 0);
  if (operation === 'majorer_pct') return valeurs.map((v) => v * (1 + valeur / 100));
  if (operation === 'ajouter_min') return valeurs.map((v, k) => (k === 0 ? v + valeur : v));
  if (operation === 'minimum_min') return total >= valeur ? valeurs : valeurs.map((v, k) => (k === 0 ? v + valeur - total : v));
  // fixer : la répartition entre lignes est GARDÉE, seul le total change.
  if (total > 0) return valeurs.map((v) => (v * valeur) / total);
  return valeurs.map((v, k) => (k === 0 ? valeur : 0));
}

/**
 * Applique des règles CHIFFRÉES à une gamme proposée. Fonction pure.
 *
 * @param {Array} gamme — les lignes proposées par l'agent
 * @param {Array<{id, scope?, effet}>} regles — déjà filtrées par portée, et
 *   dans l'ordre de VALIDATION
 * @returns {{ gamme: Array, appliques: Array }} la gamme modifiée (lignes
 *   touchées marquées `effets`), et la liste des effets appliqués avec
 *   leur avant / après. Un effet sans ligne à toucher n'est PAS appliqué,
 *   et il est rendu comme tel : un effet qui ne fait rien doit se voir.
 */
export function appliquerEffets(gamme = [], regles = []) {
  let lignes = (gamme ?? []).map((l) => ({ ...l }));
  const ordre = (r) => OPERATIONS_EFFET.indexOf(r.effet?.operation);
  const triees = (regles ?? [])
    .map((r, rang) => ({ r, rang }))
    .filter(({ r }) => effetSchema.safeParse(r.effet).success)
    .sort((a, b) => ordre(a.r) - ordre(b.r) || a.rang - b.rang)
    .map(({ r }) => r);

  const appliques = [];
  for (const regle of triees) {
    const e = regle.effet;
    const parts = partsDeLaCible(lignes, e.cible);
    if (!parts.length) {
      appliques.push({ regle_id: regle.id, effet: e, texte: decrireEffet(e), applique: false, raison: 'aucune ligne proposée pour cette cible' });
      continue;
    }
    const avant = parts.map((p) => p.lire(lignes[p.i]));
    const apres = nouvellesValeurs(avant, e).map((v) => r1(Math.max(0, v)));
    parts.forEach((p, k) => {
      if (apres[k] === r1(avant[k])) return;
      const ligne = p.ecrire(lignes[p.i], apres[k]);
      ligne.temps_min = r1(ligne.temps_min);
      ligne.effets = [...(lignes[p.i].effets ?? []), {
        regle_id: regle.id, texte: decrireEffet(e), avant: r1(avant[k]), apres: apres[k],
      }];
      lignes[p.i] = ligne;
    });
    const totalAvant = r1(avant.reduce((s, v) => s + v, 0));
    const totalApres = r1(apres.reduce((s, v) => s + v, 0));
    appliques.push({
      regle_id: regle.id, effet: e, texte: decrireEffet(e), applique: totalAvant !== totalApres,
      avant_min: totalAvant, apres_min: totalApres, lignes: parts.map((p) => lignes[p.i].op_num ?? null),
    });
  }
  return { gamme: lignes, appliques };
}
